import React from 'react';
import { getIn } from 'formik';
import ImageCropField from '../../../components/ImageCropField';
import { Chips } from '../components/Chips';
import { Field } from '../components/Field';
import { ApplicationStepProps } from '../interfaces';

/**
 * Los tres valores reales de `BenefitTypeEnum`, con las mismas reglas para el campo
 * "Valor" que ya usa el backoffice en `BenefitTypeDetail`:
 *
 *  - `discount`: sólo dígitos, 0 a 100, y se guarda con el sufijo `%`.
 *  - `promo`: texto libre y corto (`2x1`, `Consultá`).
 *  - `exchange`: **sin valor**. El backoffice no muestra el campo y en producción los
 *    beneficios de canje no lo tienen.
 *
 * El handoff proponía cuatro chips (Descuento % · Monto fijo · 2x1 · Regalo o servicio),
 * pero el modelo tiene tres tipos y dos de esos cuatro caían en el mismo.
 */
const BENEFIT_TYPES = [
  { value: 'discount', label: 'Descuento' },
  { value: 'promo', label: 'Promoción' },
  { value: 'exchange', label: 'Canje' },
];

const MAX_DISCOUNT = 100;

/** Foto de origen: de ahí salen los tres formatos que arma el back. */
const BENEFIT_IMAGE = { width: 1600, height: 1200 };

const BenefitStep: React.FC<ApplicationStepProps> = ({ controller }) => {
  const { formik } = controller;
  /**
   * La vigencia arranca como campo de texto y pasa a `type="date"` recién al enfocarlo.
   *
   * El parche anterior dejaba el input en `date` y tapaba el `dd/mm/aaaa` del navegador
   * con `color: transparent` más un `<span>` encima. Eso superponía dos textos en cuanto
   * un motor no respetaba el color. Con el cambio de tipo hay un solo texto: el
   * `placeholder` nativo cuando está vacío, y la fecha cuando hay valor.
   */
  const [isDateEditing, setIsDateEditing] = React.useState(false);

  const error = (field: string) =>
    getIn(formik.touched, field) && getIn(formik.errors, field)
      ? (getIn(formik.errors, field) as string)
      : undefined;

  const inputClass = (field: string) =>
    `bk-input${error(field) ? ' bk-input--error' : ''}`;

  const isExchange = formik.values.benefitType === 'exchange';

  return (
    <div className="bk-fields">
      <div className="bk-field">
        <span className="bk-label">Tipo de beneficio</span>
        <Chips
          label="Tipo de beneficio"
          mode="single"
          options={BENEFIT_TYPES}
          selected={[formik.values.benefitType]}
          onToggle={controller.setBenefitType}
        />
        {error('benefitType') && (
          <span className="bk-error">{error('benefitType')}</span>
        )}
      </div>

      {/* El canje no lleva valor: en ese caso el título ocupa la fila entera. */}
      <div className={isExchange ? 'bk-fields' : 'bk-pair bk-pair--value'}>
        {formik.values.benefitType === 'discount' && (
          <Field id="benefitTag" label="Valor" error={error('benefitTag')}>
            <div className="bk-suffix">
              <input
                id="benefitTag"
                className={inputClass('benefitTag')}
                type="number"
                min={0}
                max={MAX_DISCOUNT}
                value={formik.values.benefitTag.replace('%', '')}
                onChange={(event) => {
                  const digits = event.target.value.replace(/[^0-9]/g, '');

                  formik.setFieldValue(
                    'benefitTag',
                    digits === ''
                      ? ''
                      : `${Math.min(MAX_DISCOUNT, Number(digits))}%`,
                    true
                  );
                }}
                onBlur={formik.handleBlur}
                placeholder="15"
              />
              <span className="bk-suffix__unit" aria-hidden="true">
                %
              </span>
            </div>
          </Field>
        )}

        {formik.values.benefitType === 'promo' && (
          <Field id="benefitTag" label="Valor" error={error('benefitTag')}>
            <input
              id="benefitTag"
              name="benefitTag"
              className={inputClass('benefitTag')}
              maxLength={20}
              value={formik.values.benefitTag}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="2x1"
            />
          </Field>
        )}

        <Field
          id="benefitTitle"
          label="Título del beneficio"
          error={error('benefitTitle')}
        >
          <input
            id="benefitTitle"
            name="benefitTitle"
            className={inputClass('benefitTitle')}
            maxLength={80}
            value={formik.values.benefitTitle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="20% OFF en indumentaria"
          />
        </Field>
      </div>

      <Field
        id="benefitDescription"
        label="Descripción"
        error={error('benefitDescription')}
      >
        <textarea
          id="benefitDescription"
          name="benefitDescription"
          className={`bk-textarea${
            error('benefitDescription') ? ' bk-textarea--error' : ''
          }`}
          rows={3}
          maxLength={500}
          value={formik.values.benefitDescription}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="En qué consiste el beneficio y cómo lo usa el ciclista."
        />
      </Field>

      <div className="bk-pair">
        <Field
          id="pointsCost"
          label="Puntos Bikleta"
          optional
          error={error('pointsCost')}
          help="Déjalo en 0 si no hace falta canjear puntos."
        >
          <input
            id="pointsCost"
            name="pointsCost"
            type="number"
            min={0}
            step={50}
            className={inputClass('pointsCost')}
            value={formik.values.pointsCost ?? ''}
            onChange={(event) =>
              formik.setFieldValue(
                'pointsCost',
                event.target.value === '' ? null : Number(event.target.value),
                true
              )
            }
            onBlur={formik.handleBlur}
            placeholder="0"
          />
        </Field>
        <Field id="endDate" label="Vigencia hasta" optional>
          <input
            id="endDate"
            name="endDate"
            type={isDateEditing || formik.values.endDate ? 'date' : 'text'}
            className="bk-input"
            value={formik.values.endDate}
            placeholder="Sin vencimiento"
            onFocus={(event) => {
              const input = event.target;
              setIsDateEditing(true);

              // El input es `date` recién en el render siguiente: sin esto hace falta un
              // segundo click para que se abra el calendario.
              requestAnimationFrame(() => {
                try {
                  (input as any).showPicker?.();
                } catch {
                  // Los navegadores sin `showPicker` abren igual al hacer click.
                }
              });
            }}
            onBlur={(event) => {
              setIsDateEditing(false);
              formik.handleBlur(event);
            }}
            onChange={formik.handleChange}
          />
        </Field>
      </div>

      <div className="bk-field bk-upload">
        <span className="bk-label">Foto del beneficio</span>
        <ImageCropField
          outputWidth={BENEFIT_IMAGE.width}
          outputHeight={BENEFIT_IMAGE.height}
          maxFileSizeMB={10}
          fileName="beneficio.jpg"
          onChange={(file) => {
            formik.setFieldTouched('benefitFile', true, false);
            formik.setFieldValue('benefitFile', file, true);
          }}
        />
        {error('benefitFile') && (
          <span className="bk-error">{error('benefitFile')}</span>
        )}
      </div>
    </div>
  );
};

export default BenefitStep;
